import type { Locale } from "./config";
import type { Messages } from "./messages";

/**
 * Entry names are stored in English with an optional Chinese field. In Chinese
 * the Chinese name leads and the English original stays visible as a subtitle;
 * other languages show the English name only.
 */
export const localizedName = (locale: Locale, en: string, cn?: string | null, it?: string | null) =>
  locale === "zh" && cn
    ? { primary: cn, secondary: en as string | null, primaryLang: "zh-Hans", secondaryLang: "en" }
    : locale === "it" && it
      ? { primary: it, secondary: en as string | null, primaryLang: "it", secondaryLang: "en" }
      : { primary: en, secondary: null as string | null, primaryLang: "en", secondaryLang: "en" };

/** Italian name/title stored in translations.it.name (materials) or .title (applications). */
export const italianName = (entry: { translations?: unknown } | null | undefined, field: "name" | "title") => {
  const v = (entry?.translations as Partial<Record<string, Record<string, unknown>>> | undefined)?.it?.[field];
  return typeof v === "string" && v.trim() ? v : null;
};

export const applicationTypeLabel = (m: Messages, type?: string | null) =>
  (type && m.applicationTypes[type.toLowerCase()]) || type || m.common.general;

type TranslationMap = Partial<Record<Locale, Record<string, unknown>>>;

/**
 * Picks a translated field from an entry's `translations` JSON, falling back
 * to the English original. `lang` says which language the returned text is in.
 */
export const translatedText = (
  entry: { translations?: unknown } | null | undefined,
  field: string,
  original: string | null | undefined,
  locale: Locale,
): { text: string | null; lang: string; translated: boolean } => {
  const value = (entry?.translations as TranslationMap | undefined)?.[locale]?.[field];
  if (locale !== "en" && typeof value === "string" && value.trim()) {
    return { text: value, lang: locale === "zh" ? "zh-Hans" : locale, translated: true };
  }
  return { text: original ?? null, lang: "en", translated: false };
};

/** Same as translatedText for string-array fields (e.g. key_features). */
export const translatedList = (
  entry: { translations?: unknown } | null | undefined,
  field: string,
  original: string[] | null | undefined,
  locale: Locale,
): { items: string[]; lang: string; translated: boolean } => {
  const value = (entry?.translations as TranslationMap | undefined)?.[locale]?.[field];
  if (locale !== "en" && Array.isArray(value) && value.some((v) => typeof v === "string" && v.trim())) {
    return { items: value.filter((v): v is string => typeof v === "string" && !!v.trim()), lang: locale === "zh" ? "zh-Hans" : locale, translated: true };
  }
  return { items: original ?? [], lang: "en", translated: false };
};
